import React, { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BreadcrumbItem } from "@/components/ui/breadcrumbs";
import NavigationHeader from "@/components/ui/navigation-header";
// import UserMenu from "@/components/ui/user-menu";
import {
  Users,
  Package,
  Utensils,

  // Menu,
  X,
  Shield,
  CreditCard,
  Home,
} from "lucide-react";
// import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

/**
 * Layout específico para la sección de administración
 *
 * Incluye una barra lateral de navegación responsive con enlaces a:
 * - Gestión de Usuarios
 * - Gestión de Productos
 * - Gestión de Menús
 * - Breadcrumbs dinámicos
 */
const AdminLayout: React.FC = () => {
  // const { user } = useAuth();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Generar breadcrumbs dinámicamente basado en la ruta
  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split("/").filter(Boolean);

    if (pathSegments.length === 0) return [];

    const breadcrumbs: BreadcrumbItem[] = [];
    let currentPath = "";

    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      // Mapear segmentos a nombres legibles
      const segmentMap: Record<string, string> = {
        admin: "Administración",
        users: "Gestión de Usuarios",
        products: "Gestión de Productos",
        menus: "Gestión de Menús",
        transactions: "Gestión de Transacciones",
        reports: "Reportes",
        settings: "Configuración",
        test: "Pruebas",
      };

      const label =
        segmentMap[segment] ||
        segment.charAt(0).toUpperCase() + segment.slice(1);

      // Para la ruta /admin, siempre mostrar como página actual
      if (segment === "admin" && pathSegments.length === 1) {
        breadcrumbs.push({
          label,
          current: true,
        });
      } else {
        breadcrumbs.push({
          label,
          href: index === pathSegments.length - 1 ? undefined : currentPath,
          current: index === pathSegments.length - 1,
        });
      }
    });

    return breadcrumbs;
  };

  // Logo personalizado para administración
  const adminLogo = (
    <div className="flex items-center space-x-2">
      <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center shadow-sm">
        <Shield className="h-6 w-6 text-primary-foreground" />
      </div>
      <div>
        <h1 className="text-lg md:text-xl font-bold text-card-foreground">
          Rincón Chilenito
        </h1>
        <p className="text-xs text-muted-foreground hidden sm:block text-left">
          Administración
        </p>
      </div>
    </div>
  );

  /**
   * Cierra el sidebar en mobile
   */
  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header Sticky */}
      <div className="sticky top-0 z-50 bg-background border-b border-border">
        <NavigationHeader
          variant="admin"
          title="Chilenito SPA"
          subtitle="Administración"
          logo={adminLogo}
          breadcrumbs={getBreadcrumbs()}
          showCart={false}
          showThemeToggle={true}
          showUserMenu={true}
          userMenuVariant="compact"
          showMobileMenu={true}
          onMobileMenuClick={() => setIsSidebarOpen(true)}
        />
      </div>

      <div className="flex flex-1">
        {/* Sidebar Sticky - Desktop */}
        <aside className="hidden md:flex md:flex-col w-64 bg-sidebar border-r border-border sticky top-[73px] h-[calc(100vh-73px)] overflow-y-auto">
          <nav className="p-4 space-y-2 flex-1">
            <h2 className="text-sm font-semibold text-sidebar-foreground mb-4 px-2">
              ADMINISTRACIÓN
            </h2>
            <div className="space-y-2">

            {/* Enlace al Dashboard */}
            <NavLink
              to="/admin"
              end
              onClick={closeSidebar}
              className={({ isActive }) =>
                cn(
                  "flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group",
                  isActive
                    ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
                    : "text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm"
                )
              }
            >
              <Home className="h-4 w-4 transition-transform group-hover:scale-110" />
              <span>Dashboard</span>
            </NavLink>

            {/* Usuarios */}
            <NavLink
              to="/admin/users"
              className={({ isActive }) =>
                cn(
                  "flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group",
                  isActive
                    ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
                    : "text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm"
                )
              }
            >
              <Users className="h-4 w-4 transition-transform group-hover:scale-110" />
              <span>Usuarios</span>
            </NavLink>

            {/* Productos */}
            <NavLink
              to="/admin/products"
              className={({ isActive }) =>
                cn(
                  "flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group",
                  isActive
                    ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
                    : "text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm"
                )
              }
            >
              <Package className="h-4 w-4 transition-transform group-hover:scale-110" />
              <span>Productos</span>
            </NavLink>

            {/* Menús */}
            <NavLink
              to="/admin/menus"
              className={({ isActive }) =>
                cn(
                  "flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group",
                  isActive
                    ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
                    : "text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm"
                )
              }
            >
              <Utensils className="h-4 w-4 transition-transform group-hover:scale-110" />
              <span>Menús</span>
            </NavLink>

            {/* Transacciones */}
            <NavLink
              to="/admin/transactions"
              className={({ isActive }) =>
                cn(
                  "flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group",
                  isActive
                    ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
                    : "text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm"
                )
              }
            >
              <CreditCard className="h-4 w-4 transition-transform group-hover:scale-110" />
              <span>Transacciones</span>
            </NavLink>
            </div>
          </nav>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {isSidebarOpen && (
          <div className="fixed inset-0 z-[60] md:hidden">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={closeSidebar}
              aria-hidden="true"
            />
            <aside className="fixed left-0 top-0 z-[70] h-full w-64 border-r border-border bg-sidebar shadow-xl">
              <div className="flex h-16 items-center justify-between px-4 border-b border-sidebar-border bg-sidebar">
                <h2 className="text-lg font-semibold text-sidebar-foreground">
                  Administración
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeSidebar}
                  className="h-8 w-8 p-0 text-sidebar-foreground hover:bg-sidebar-accent"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <nav className="p-4 space-y-2 overflow-y-auto max-h-[calc(100vh-120px)]">
                {/* Enlace al Dashboard */}
                <NavLink
                  to="/admin"
                  end
                  onClick={closeSidebar}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 group",
                      isActive
                        ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
                        : "text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm"
                    )
                  }
                >
                  <Home className="h-5 w-5 transition-transform group-hover:scale-110" />
                  <span>Dashboard</span>
                </NavLink>

                {/* Usuarios */}
                <NavLink
                  to="/admin/users"
                  onClick={closeSidebar}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 group",
                      isActive
                        ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
                        : "text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm"
                    )
                  }
                >
                  <Users className="h-5 w-5 transition-transform group-hover:scale-110" />
                  <span>Usuarios</span>
                </NavLink>

                {/* Productos */}
                <NavLink
                  to="/admin/products"
                  onClick={closeSidebar}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 group",
                      isActive
                        ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
                        : "text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm"
                    )
                  }
                >
                  <Package className="h-5 w-5 transition-transform group-hover:scale-110" />
                  <span>Productos</span>
                </NavLink>

                {/* Menús */}
                <NavLink
                  to="/admin/menus"
                  onClick={closeSidebar}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 group",
                      isActive
                        ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
                        : "text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm"
                    )
                  }
                >
                  <Utensils className="h-5 w-5 transition-transform group-hover:scale-110" />
                  <span>Menús</span>
                </NavLink>

                {/* Transacciones */}
                <NavLink
                  to="/admin/transactions"
                  onClick={closeSidebar}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 group",
                      isActive
                        ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
                        : "text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm"
                    )
                  }
                >
                  <CreditCard className="h-5 w-5 transition-transform group-hover:scale-110" />
                  <span>Transacciones</span>
                </NavLink>
              </nav>
            </aside>
          </div>
        )}

        {/* Contenido Principal */}
        <main className="flex-1 overflow-y-auto">
          <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
